// Single shared default used everywhere: print, client view, email, download.
// Ensures what you see in print = what the client gets.
import { sanitizeEstimateDescription } from '../../lib/estimate-notes';

export const DEFAULT_PDF_TEMPLATE_CONFIG: PdfTemplateConfig = {
  colorPrimary: '#0f766e',
  colorSecondary: '#f0fdfa',
  colorText: '#1e293b',
  colorAccent: '#0f766e',
  colorTableHeader: '#0f766e',
  colorTableBorder: '#e2e8f0',
  colorTableEven: '#f8fafc',
  fontHeading: "'Inter', 'Helvetica Neue', Arial, sans-serif",
  fontBody: "'Inter', 'Helvetica Neue', Arial, sans-serif",
  showLogo: true,
  showHeader: true,
  showFooter: false,
  showTax: true,
  showDiscount: true,
  showSignature: true,
  showNotes: true,
  showTerms: true,
  showClientDetails: true,
  showDates: true,
  showProjectDetails: false,
  showItemDetails: true,
  headerStyle: 'simple',
  tableStyle: 'striped',
  roundedCorners: true,
  alternateRowColors: true,
  footerText: 'Thank you for your business',
  template: 'professional',
};

// Comprehensive PDF Template Config Interface
export interface PdfTemplateConfig {
  // Colors
  colorPrimary?: string;
  colorSecondary?: string;
  colorText?: string;
  colorAccent?: string;
  colorTableHeader?: string;
  colorTableBorder?: string;
  colorTableEven?: string;
  
  // Fonts
  fontHeading?: string;
  fontBody?: string;
  fontMain?: string;
  textSize?: string;
  lineHeight?: number;
  
  // Layout
  logoPosition?: string;
  companyInfoPosition?: string;
  documentTitle?: string;
  pageSize?: string;
  pageOrientation?: string;
  
  // Header/Footer
  showLogo?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  footerText?: string;
  headerStyle?: 'simple' | 'gradient' | 'boxed';
  useHeaderImage?: boolean;
  headerImageUrl?: string;
  
  // Table styling
  tableStyle?: 'striped' | 'bordered' | 'minimal';
  tableBorderStyle?: string;
  roundedCorners?: boolean;
  alternateRowColors?: boolean;
  
  // Content visibility
  showTax?: boolean;
  showDiscount?: boolean;
  showSignature?: boolean;
  showNotes?: boolean;
  showTerms?: boolean;
  showItemDescription?: boolean;
  showItemQuantity?: boolean;
  showItemUnitPrice?: boolean;
  showItemUnit?: boolean;
  showItemAmount?: boolean;
  showItemDetails?: boolean;
  showItemNotes?: boolean;
  showProjectDetails?: boolean;
  showClientDetails?: boolean;
  showDates?: boolean;
  showSignatureLine?: boolean;
  
  // Column configuration
  showColumns?: {
    service?: boolean;
    description?: boolean;
    quantity?: boolean;
    unitPrice?: boolean;
    amount?: boolean;
    notes?: boolean;
  };
  
  // Column titles
  columnTitles?: {
    service?: string;
    description?: string;
    quantity?: string;
    unitPrice?: string;
    amount?: string;
    notes?: string;
  };
  
  // Date fields
  enableEstimateDateField?: boolean;
  enableEstimateExpiry?: boolean;
  enableInvoiceDueDate?: boolean;
  dateFormat?: string;
  
  // Watermark
  enableWatermark?: boolean;
  customWatermarkText?: string;
  
  // Template name
  template?: string;
  customCSS?: string;
}

export interface EstimateData {
  id?: number;
  estimateNumber?: string;
  invoiceNumber?: string;
  issueDate?: string | Date;
  expiryDate?: string | Date;
  dueDate?: string | Date;
  status?: string;
  client?: {
    firstName?: string;
    lastName?: string;
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  contractor?: {
    companyName?: string;
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    website?: string;
    logo?: string;
    logoUrl?: string; // Support both logo and logoUrl for compatibility
  };
  items?: Array<{
    id?: string | number;
    serviceType?: string;
    service?: string;
    description?: string;
    quantity?: number;
    unit?: string;
    unitPrice?: number;
    amount?: number;
    notes?: string;
  }>;
  subtotal?: number;
  tax?: number;
  taxRate?: number;
  taxAmount?: number;
  discount?: number;
  total?: number;
  notes?: string;
  terms?: string;
  description?: string;
  clientSignature?: string;
  contractorSignature?: string;
  amountPaid?: number;
}

interface UnifiedPdfTemplateProps {
  data: EstimateData;
  config: PdfTemplateConfig;
  type: 'estimate' | 'invoice';
  isSigned?: boolean;
}

// Canonical visual: always applied so we get the design we want.
// Overrides any old/incomplete saved template colors and layout.
const CANONICAL_VISUAL_OVERRIDES: Partial<PdfTemplateConfig> = {
  colorPrimary: '#0f766e',
  colorSecondary: '#f0fdfa',
  colorText: '#1e293b',
  colorAccent: '#0f766e',
  colorTableHeader: '#0f766e',
  colorTableBorder: '#e2e8f0',
  colorTableEven: '#f8fafc',
  fontHeading: "'Inter', 'Helvetica Neue', Arial, sans-serif",
  fontBody: "'Inter', 'Helvetica Neue', Arial, sans-serif",
  headerStyle: 'simple',
  tableStyle: 'striped',
  roundedCorners: true,
  alternateRowColors: true,
};

export function generatePdfHtml(props: UnifiedPdfTemplateProps): string {
  const { data, type, isSigned } = props;
  const config: PdfTemplateConfig = {
    ...DEFAULT_PDF_TEMPLATE_CONFIG,
    ...props.config,
    ...CANONICAL_VISUAL_OVERRIDES,
  };
  
  // Local formatCurrency function
  const formatCurrencyLocal = (amount: number | string | undefined | null): string => {
    if (amount === undefined || amount === null) return "$0.00";
    try {
      const numericAmount = typeof amount === 'string' 
        ? parseFloat(amount.replace(/[$,]/g, '')) 
        : amount;
      if (isNaN(numericAmount)) return "$0.00";
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numericAmount);
    } catch (error) {
      return "$0.00";
    }
  };

  // Import formatPhone from validation utilities
  // Note: Using inline function since this is a template string function
  const formatPhone = (phone: string | null | undefined): string => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return phone; // Return as-is if not 10 digits
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  
  // Extract config with defaults
  // Contractor-friendly defaults (research: navy/teal/forest green, high contrast, 2-3 colors)
  const colorPrimary = config.colorPrimary || '#0f766e';
  const colorSecondary = config.colorSecondary || '#f0fdfa';
  const colorText = config.colorText || '#1e293b';
  const colorAccent = config.colorAccent || colorPrimary;
  const colorTableHeader = config.colorTableHeader || colorPrimary;
  const colorTableBorder = config.colorTableBorder || '#e2e8f0';
  const colorTableEven = config.colorTableEven || '#f8fafc';
  
  const fontHeading = config.fontHeading || config.fontMain || "'Inter', 'Helvetica Neue', Arial, sans-serif";
  const fontBody = config.fontBody || config.fontMain || "'Inter', 'Helvetica Neue', Arial, sans-serif";
  const lineHeight = config.lineHeight ?? 1.6;
  
  const showLogo = config.showLogo !== false;
  const showHeader = config.showHeader !== false;
  const showFooter = false;
  const showTax = config.showTax !== false;
  const showDiscount = config.showDiscount !== false;
  const showSignature = config.showSignature !== false;
  const showNotes = config.showNotes !== false;
  const showTerms = config.showTerms !== false;
  const showDates = config.showDates !== false;
  const showClientDetails = config.showClientDetails !== false;
  // showProjectDetails controls the standalone "Project Description" section - default false to avoid duplication
  // Description should only appear in the Services & Pricing table's Description column
  const showProjectDetails = config.showProjectDetails === true; // Only show if explicitly enabled
  const showItemDetails = config.showItemDetails !== false;
  
  const headerStyle = config.headerStyle || 'simple';
  const tableStyle = config.tableStyle || 'striped';
  const roundedCorners = config.roundedCorners !== false;
  const alternateRowColors = config.alternateRowColors !== false;
  
  const footerText = config.footerText || 'Thank you for your business';
  
  // Column visibility
  const showColumns = config.showColumns || {};
  const showServiceCol = showColumns.service !== false;
  // Description column in table should show item-specific descriptions, not the main description
  // Only show if items have their own descriptions (not the main project description)
  const showDescCol = showColumns.description !== false;
  const showQtyCol = showColumns.quantity !== false;
  const showPriceCol = showColumns.unitPrice !== false;
  const showAmountCol = showColumns.amount !== false;
  const showNotesCol = showColumns.notes !== false;
  
  // Document info
  const documentNumber = type === 'estimate' ? data.estimateNumber : data.invoiceNumber;
  const documentTitle = type === 'estimate' ? 'ESTIMATE' : 'INVOICE';
  const documentType = type === 'estimate' ? 'Estimate' : 'Invoice';
  
  const contractor = data.contractor as Record<string, unknown> | undefined;
  const companyName = (contractor?.companyName ?? contractor?.company_name ?? contractor?.name) as string | undefined || 'Your Company';
  const companyAddressParts = [
    contractor?.address ?? contractor?.contractor_address,
    contractor?.city ?? contractor?.contractor_city,
    contractor?.state ? (contractor?.zip ?? contractor?.contractor_zip ? `${contractor.state} ${contractor.zip ?? contractor.contractor_zip}` : contractor.state) : contractor?.zip ?? contractor?.contractor_zip
  ].filter(Boolean).map(String);
  const companyAddressLine = companyAddressParts.length ? companyAddressParts.join(', ') : '';
  const companyPhone = (contractor?.phone ?? contractor?.contractor_phone) as string | undefined;
  const clientName = data.client?.name || `${data.client?.firstName || ''} ${data.client?.lastName || ''}`.trim() || 'Client';
  const c = data.client as Record<string, unknown> | undefined;
  const clientAddressParts = [
    c?.address ?? c?.client_address,
    c?.city ?? c?.client_city,
    c?.state ? (c.zip ?? c?.client_zip ? `${c.state} ${c.zip ?? c.client_zip}` : c.state) : c?.zip ?? c?.client_zip
  ].filter(Boolean).map(String);
  const clientAddressLine =
    clientAddressParts.length
      ? clientAddressParts.join(', ')
      : (c?.addressLine as string) || (c?.formattedAddress as string) || '';
  const showClientAddress = clientAddressLine.trim().length > 0;
  
  const dataWithTotal = data as { total_amount?: number };
  const computedSubtotal = (data.items || []).reduce((sum: number, i: { amount?: number }) => sum + (Number(i.amount) || 0), 0);
  const docSubtotal = data.subtotal ?? dataWithTotal.total_amount ?? (computedSubtotal > 0 ? computedSubtotal : 0);
  const docTotal = data.total ?? dataWithTotal.total_amount ?? (computedSubtotal > 0 ? computedSubtotal : 0);
  
  const currentDate = new Date();
  const formatDate = (date: string | Date | undefined) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return 'N/A';
    }
  };
  
  // Header styles
  const getHeaderStyles = () => {
    switch (headerStyle) {
      case 'gradient':
        return `
          border-radius: ${roundedCorners ? '8px' : '0'};
          background: linear-gradient(135deg, ${colorPrimary} 0%, ${colorAccent === colorPrimary ? 'rgba(0,0,0,0.2)' : colorAccent} 100%);
          padding: 16px 24px;
          color: #ffffff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        `;
      case 'boxed':
        return `
          border-radius: ${roundedCorners ? '8px' : '0'};
          border: 2px solid ${colorPrimary};
          padding: 14px 20px;
          background: ${colorSecondary};
          color: ${colorText};
        `;
      case 'simple':
      default:
        return `
          background: ${colorSecondary};
          border: 1px solid ${colorTableBorder};
          border-radius: 8px;
          padding: 18px 24px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.06);
        `;
    }
  };
  
  // Table styles
  const getTableStyles = () => {
    const baseStyles = `
      width: 100%;
      border-collapse: collapse;
    `;
    
    switch (tableStyle) {
      case 'striped':
        return baseStyles + `
          .items-table tbody tr:nth-child(even) {
            background-color: ${colorTableEven};
          }
        `;
      case 'minimal':
        return baseStyles + `
          .items-table th, .items-table td {
            border: none;
            border-bottom: 1px solid ${colorTableBorder};
          }
        `;
      case 'bordered':
      default:
        return baseStyles + `
          .items-table th, .items-table td {
            border: 1px solid ${colorTableBorder};
          }
        `;
    }
  };

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${documentType} ${documentNumber}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        html {
          min-height: 100vh;
          height: 100%;
        }
        body { 
          font-family: ${fontBody};
          margin: 0; 
          padding: 28px; 
          line-height: 1.5; 
          color: ${colorText};
          background: #f1f5f9;
          font-size: 12px;
          min-height: 100%;
        }
        
        .pdf-body {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          min-height: 100%;
          flex: 1 1 0%;
        }
        .pdf-main {
          flex: 1 1 0%;
          min-height: 0;
        }
        .pdf-bottom {
          flex: 0 0 auto;
          padding-top: 48px;
        }
        
        .top-row { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 24px; 
          align-items: start;
          margin-bottom: 24px;
        }
        .company-block .company-name { 
          font-family: ${fontHeading};
          font-size: 16px; 
          font-weight: 700; 
          color: ${colorText};
          margin-bottom: 6px;
        }
        .company-block .doc-logo { 
          margin-bottom: 10px;
          min-height: 48px;
        }
        .company-block .doc-logo img { 
          display: block; 
          max-height: 56px; 
          max-width: 180px; 
          object-fit: contain;
        }
        .company-block .company-address,
        .company-block .company-contact { 
          font-size: 12px; 
          color: ${colorText};
          line-height: 1.5;
        }
        .doc-branding { 
          text-align: right;
        }
        .doc-branding .doc-logo { 
          margin-bottom: 10px;
          min-height: 48px;
        }
        .doc-branding .doc-logo img { 
          display: block; 
          max-height: 56px; 
          max-width: 180px; 
          object-fit: contain;
          margin-left: auto;
        }
        .doc-branding .document-title { 
          font-family: ${fontHeading};
          font-size: 28px; 
          font-weight: 700; 
          color: ${colorPrimary};
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .bill-to-row { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 24px; 
          align-items: start;
          margin-bottom: 20px;
        }
        .bill-to-label { 
          font-size: 13px; 
          font-weight: 600; 
          color: ${colorPrimary}; 
          margin-bottom: 8px;
        }
        .client-name { 
          font-weight: 700; 
          color: ${colorText};
          font-size: 13px;
          margin-bottom: 4px;
        }
        .client-address { 
          color: ${colorText};
          font-size: 12px;
          line-height: 1.5;
        }
        .doc-details { 
          text-align: right;
          min-width: 200px;
        }
        .doc-details .doc-detail-row { 
          display: flex;
          justify-content: flex-end;
          align-items: baseline;
          gap: 10px;
          margin-bottom: 6px; 
          font-size: 12px;
        }
        .doc-details .doc-detail-label { 
          color: ${colorPrimary}; 
          font-weight: 600;
          min-width: 82px;
          text-align: right;
        }
        .doc-details .doc-detail-value { 
          color: ${colorText};
          min-width: 120px;
          text-align: left;
        }
        
        .section { margin-bottom: 20px; }
        .section-title { 
          font-family: ${fontHeading};
          font-size: 14px; 
          font-weight: 600; 
          color: ${colorPrimary}; 
          margin-bottom: 10px;
          padding-bottom: 6px;
          border-bottom: 2px solid ${colorPrimary};
          letter-spacing: 0.3px;
        }
        /* Tight spacing between Services & Pricing heading and the table */
        .section-services-pricing .section-title { margin-bottom: 4px; padding-bottom: 4px; }
        .section-services-pricing .table-wrapper { margin-top: 0; }
        .info-item { margin-bottom: 8px; }
        .info-item:last-child { margin-bottom: 0; }
        .info-label { font-weight: 600; color: ${colorText}; font-size: 12px; }
        
        .table-wrapper { 
          background: #ffffff; 
          border: 1px solid ${colorTableBorder}; 
          border-radius: 10px; 
          overflow: hidden; 
          box-shadow: 0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
          margin-bottom: 20px;
        }
        
        .items-table { ${getTableStyles()} }
        
        .items-table th, .items-table td { 
          padding: 10px 14px; 
          text-align: left; 
          vertical-align: top;
          font-size: 12px;
          border: 1px solid ${colorTableBorder};
        }
        
        .items-table td.item-desc { 
          font-size: 10px; 
          line-height: 1.4;
          max-height: 88px;
          overflow: hidden;
        }
        
        .items-table th.num-col,
        .items-table td.num-col { text-align: right; white-space: nowrap; }
        
        .items-table thead th { 
          background-color: ${colorPrimary}; 
          font-weight: 600;
          color: #ffffff;
          font-family: ${fontHeading};
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          border: 1px solid ${colorPrimary};
          padding: 10px 14px;
        }
        
        ${alternateRowColors && tableStyle === 'striped' ? `
        .items-table tbody tr:nth-child(even) { background-color: ${colorTableEven}; }
        ` : ''}
        
        .totals-signature-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          align-items: start;
          margin-top: 20px;
          margin-bottom: 20px;
        }
        .totals-signature-row .signature-section { margin-top: 0; }
        .totals-signature-row .total-wrap { margin-bottom: 0; text-align: right; }
        
        .total-wrap { text-align: right; margin-bottom: 20px; }
        .invoice-footer-inline { page-break-inside: avoid; }
        .invoice-footer-inline .signature-section { max-width: 400px; }
        
        .total-section { 
          display: inline-block;
          text-align: right; 
          padding: 18px 26px;
          min-width: 260px;
          background: #ffffff;
          border: 1px solid ${colorTableBorder};
          border-left: 4px solid ${colorPrimary};
          border-radius: 10px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
        }
        
        .total-line { margin-bottom: 6px; font-size: 12px; }
        .total-amount { 
          font-size: 22px; 
          font-weight: 700; 
          color: ${colorPrimary};
          margin-top: 8px;
          padding-top: 8px;
          border-top: 2px solid ${colorTableBorder};
        }
        
        .signature-section { 
          margin-top: 28px; 
          padding-top: 0;
        }
        
        .signature-container { margin-top: 16px; }
        
        .signature-box { 
          width: 240px; 
          height: 80px; 
          border: 2px solid ${colorTableBorder}; 
          border-radius: 8px;
          padding: 8px;
          background: #ffffff;
          margin: 0 auto;
          box-shadow: 0 2px 4px rgba(0,0,0,0.06);
        }
        
        .signature-box-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          font-style: italic;
          font-size: 12px;
        }
        
        .signature-image { width: 100%; height: 100%; object-fit: contain; display: block; }
        .signature-details { margin-top: 8px; font-size: 12px; text-align: center; }
        
        .legal-notice { 
          margin-top: 24px; 
          padding: 16px 20px; 
          background: ${colorSecondary}; 
          border: 1px solid ${colorPrimary}; 
          border-radius: 8px;
          font-size: 11px;
          color: ${colorText};
          line-height: 1.5;
        }
        
        .footer { 
          margin-top: 28px; 
          text-align: center; 
          font-size: 11px; 
          color: #64748b;
          background: #ffffff;
          border: 1px solid ${colorTableBorder};
          border-radius: 8px;
          padding: 16px 20px;
          line-height: 1.5;
        }
        
        .section-notes, .section-terms { 
          background: #ffffff; 
          border: 1px solid ${colorTableBorder}; 
          border-radius: 8px; 
          padding: 16px 20px; 
          box-shadow: 0 2px 4px rgba(0,0,0,0.06);
          font-size: 12px;
          line-height: 1.5;
        }
        .section-notes {
          max-width: 400px;
        }
        
        .watermark {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 120px;
          color: rgba(0,0,0,0.05);
          font-weight: bold;
          pointer-events: none;
          z-index: 9999;
        }
        
        @media print { 
          html, body { 
            min-height: 0;
            height: auto;
          }
          /* Print should flow naturally; flex + min-heights cause large blank areas */
          .pdf-body {
            display: block !important;
            min-height: 0 !important;
          }
          .pdf-main {
            flex: none !important;
            min-height: auto !important;
          }
          .pdf-bottom {
            flex: none !important;
            padding-top: 0 !important;
          }
          body { 
            margin: 0; 
            padding: 0; 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact; 
          }
          /* Tight gap between "Services & Pricing" and the table when printing professional template */
          .section-services-pricing .section-title { margin-bottom: 4px !important; padding-bottom: 4px !important; }
          .section-services-pricing .table-wrapper { margin-top: 0 !important; }
          .top-row { page-break-inside: avoid; }
          .top-row,
          .bill-to-row,
          .table-wrapper,
          .total-wrap,
          .total-section,
          .footer,
          .section-notes,
          .section-terms,
          .legal-notice { 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact; 
          }
          .items-table thead th { 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact; 
          }
          .signature-section,
          .totals-signature-row,
          .invoice-footer-inline { 
            page-break-inside: avoid; 
            page-break-before: avoid;
          }
          @page {
            size: ${config.pageSize === 'a4' ? 'A4' : 'letter'} ${config.pageOrientation || 'portrait'};
            margin: 0.5in;
          }
        }
        
        ${config.customCSS || ''}
      </style>
    </head>
    <body class="pdf-body">
      ${config.enableWatermark && config.customWatermarkText ? `
      <div class="watermark">${config.customWatermarkText}</div>
      ` : ''}
      
      <div class="pdf-main">
      ${showHeader ? `
      <div class="top-row">
        <div class="company-block">
          ${showLogo && (data.contractor?.logoUrl || data.contractor?.logo) ? `<div class="doc-logo"><img src="${data.contractor.logoUrl || data.contractor.logo}" alt="${companyName}" /></div>` : ''}
          <div class="company-name">${companyName}</div>
          ${companyAddressLine ? `<div class="company-address">${companyAddressLine}</div>` : ''}
          ${companyPhone ? `<div class="company-contact">${formatPhone(companyPhone)}</div>` : ''}
        </div>
        <div class="doc-branding">
          <div class="document-title">${
            isSigned
              ? (type === 'invoice' ? 'Signed Invoice' : 'Signed Estimate')
              : documentTitle
          }</div>
        </div>
      </div>
      ` : ''}
      
      ${showClientDetails ? `
      <div class="bill-to-row">
        <div class="bill-to-block">
          <div class="bill-to-label">${type === 'invoice' ? 'Bill To' : 'Client'}</div>
          <div class="client-name">${clientName}</div>
          <div class="client-address">${showClientAddress ? clientAddressLine : '—'}</div>
        </div>
        <div class="doc-details">
          <div class="doc-detail-row"><span class="doc-detail-label">${documentType} #</span><span class="doc-detail-value">${documentNumber}</span></div>
          ${showDates && data.issueDate ? `<div class="doc-detail-row"><span class="doc-detail-label">${type === 'invoice' ? 'Invoice date' : 'Issue date'}</span><span class="doc-detail-value">${formatDate(data.issueDate)}</span></div>` : ''}
          ${showDates && type === 'estimate' && data.expiryDate && config.enableEstimateExpiry ? `<div class="doc-detail-row"><span class="doc-detail-label">Expiry date</span><span class="doc-detail-value">${formatDate(data.expiryDate)}</span></div>` : ''}
          ${showDates && type === 'invoice' && data.dueDate && config.enableInvoiceDueDate ? `<div class="doc-detail-row"><span class="doc-detail-label">Due date</span><span class="doc-detail-value">${formatDate(data.dueDate)}</span></div>` : ''}
          ${isSigned ? `<div class="doc-detail-row"><span class="doc-detail-value"><strong>ACCEPTED & SIGNED</strong></span></div>` : ''}
        </div>
      </div>
      ` : ''}
      
      ${showProjectDetails && data.description ? `
      <div class="section">
        <div class="section-title">Project Description</div>
        <p style="line-height: 1.8; color: ${colorText};">${data.description}</p>
      </div>
      ` : ''}
      
      <div class="section section-services-pricing">
        <div class="section-title">Services & Pricing</div>
        <div class="table-wrapper">
        <table class="items-table">
          <thead>
            <tr>
              ${showServiceCol ? '<th>Service Type</th>' : ''}
              ${showDescCol ? '<th>Description</th>' : ''}
              ${showQtyCol ? '<th class="num-col">Quantity</th>' : ''}
              ${showPriceCol ? '<th class="num-col">Unit Price</th>' : ''}
              ${showAmountCol ? '<th class="num-col">Amount</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${(data.items || []).map(item => {
              const itemDescription = sanitizeEstimateDescription(item.description || item.serviceType || item.service || '');
              return `
              <tr>
                ${showServiceCol ? `<td><strong>${item.serviceType || item.service || 'Service'}</strong></td>` : ''}
                ${showDescCol ? `<td class="item-desc">${itemDescription}</td>` : ''}
                ${showQtyCol ? `<td class="num-col">${item.quantity || 1} ${(item.unit || '').toString().trim()}</td>` : ''}
                ${showPriceCol ? `<td class="num-col">${formatCurrencyLocal(item.unitPrice || 0)}</td>` : ''}
                ${showAmountCol ? `<td class="num-col"><strong>${formatCurrencyLocal(item.amount || 0)}</strong></td>` : ''}
              </tr>
            `;
            }).join('')}
          </tbody>
        </table>
        </div>
      </div>
      
      ${type === 'invoice' ? `
      <div class="invoice-footer-inline" style="margin-top: 32px;">
        <div class="totals-signature-row">
          <div class="signature-section">
            <div class="section-title">Payment</div>
            <p style="margin-bottom: 12px; line-height: 1.6; font-size: 12px; color: ${colorText};">
              Please remit payment by the due date. Thank you for your business.
            </p>
          </div>
          <div class="total-wrap">
            <div class="total-section">
              <div class="total-line"><span>Subtotal:</span> <strong>${formatCurrencyLocal(docSubtotal)}</strong></div>
              ${showTax ? `<div class="total-line"><span>Tax${data.taxRate ? ` (${data.taxRate}%)` : ''}:</span> <strong>${formatCurrencyLocal(data.tax ?? data.taxAmount ?? 0)}</strong></div>` : ''}
              ${showDiscount ? `<div class="total-line"><span>Discount:</span> <strong>-${formatCurrencyLocal(data.discount ?? 0)}</strong></div>` : ''}
              ${(data.amountPaid ?? 0) > 0 ? `<div class="total-line"><span>Paid:</span> <strong>${formatCurrencyLocal(data.amountPaid || 0)}</strong></div><div class="total-line"><span>Balance Due:</span> <strong>${formatCurrencyLocal(Math.max(0, docTotal - (data.amountPaid || 0)))}</strong></div>` : ''}
              <div class="total-amount">${(data.amountPaid ?? 0) >= docTotal ? `Balance Due: ${formatCurrencyLocal(0)}` : formatCurrencyLocal(docTotal)}</div>
            </div>
          </div>
        </div>
      </div>
      ` : ''}
      
      ${showNotes && data.notes ? `
      <div class="section section-notes">
        <div class="section-title">Additional Notes</div>
        <p style="line-height: 1.8; color: ${colorText};">${data.notes}</p>
      </div>
      ` : ''}
      
      ${showTerms && data.terms ? `
      <div class="section section-terms">
        <div class="section-title">Terms & Conditions</div>
        <p style="line-height: 1.8; color: ${colorText};">${data.terms}</p>
      </div>
      ` : ''}
      
      </div>
      <div class="pdf-bottom">
      ${type === 'invoice' ? '' : showSignature ? `
      <div class="totals-signature-row">
        <div class="signature-section">
          <div class="section-title">${isSigned ? 'Digital Signature & Authorization' : 'Signature'}</div>
          ${isSigned && data.clientSignature ? `
          <p style="margin-bottom: 20px; line-height: 1.8;">
            By signing below, the client acknowledges that they have read, understood, and agree to the terms 
            and conditions outlined in this service ${type}. This signature constitutes acceptance of the 
            proposed work and pricing.
          </p>
          <div class="signature-container">
            <div style="text-align: center;">
              <div class="signature-box">
                <img src="${data.clientSignature}" class="signature-image" alt="Client Signature" />
              </div>
              <div class="signature-details">
                <div style="margin-bottom: 4px;"><strong>${clientName}</strong></div>
                <div style="font-size: 11px; color: #6b7280;">${currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              </div>
            </div>
          </div>
          ` : `
          <div style="text-align: center;">
            <div class="signature-box signature-box-empty">
              Signature Required
            </div>
            <div class="signature-details">
              <div style="margin-bottom: 4px;"><strong>Client Name</strong></div>
              <div style="font-size: 11px; color: #6b7280;">Date</div>
            </div>
          </div>
          `}
        </div>
        <div class="total-wrap">
          <div class="total-section">
            <div class="total-line"><span>Subtotal:</span> <strong>${formatCurrencyLocal(docSubtotal)}</strong></div>
            ${showTax ? `<div class="total-line"><span>Tax${data.taxRate ? ` (${data.taxRate}%)` : ''}:</span> <strong>${formatCurrencyLocal(data.tax ?? data.taxAmount ?? 0)}</strong></div>` : ''}
            ${showDiscount ? `<div class="total-line"><span>Discount:</span> <strong>-${formatCurrencyLocal(data.discount ?? 0)}</strong></div>` : ''}
            <div class="total-amount">${formatCurrencyLocal(docTotal)}</div>
          </div>
        </div>
      </div>
      ` : `
      <div class="total-wrap">
        <div class="total-section">
          <div class="total-line"><span>Subtotal:</span> <strong>${formatCurrencyLocal(docSubtotal)}</strong></div>
          ${showTax ? `<div class="total-line"><span>Tax${data.taxRate ? ` (${data.taxRate}%)` : ''}:</span> <strong>${formatCurrencyLocal(data.tax ?? data.taxAmount ?? 0)}</strong></div>` : ''}
          ${showDiscount ? `<div class="total-line"><span>Discount:</span> <strong>-${formatCurrencyLocal(data.discount ?? 0)}</strong></div>` : ''}
          <div class="total-amount">${formatCurrencyLocal(docTotal)}</div>
        </div>
      </div>
      `}
      ${isSigned ? `
      <div class="legal-notice">
        <strong>Legal Notice:</strong> This signed ${type === 'invoice' ? 'invoice' : 'estimate'} represents the client's approval of the work, pricing, and terms outlined above between
        ${companyName} and the client. The client's digital signature confirms acceptance of these terms and authorizes the work to proceed.
      </div>
      ` : ''}
      ${showFooter ? `
      <div class="footer">
        <p><strong>${companyName}</strong></p>
        <p>${footerText}</p>
        ${data.contractor?.email ? `<p>${data.contractor.email}</p>` : ''}
        ${data.contractor?.phone ? `<p>${formatPhone(data.contractor.phone)}</p>` : ''}
        <p style="margin-top: 10px; font-size: 10px;">Generated on ${currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      </div>
      ` : ''}
      </div>
    </body>
    </html>
  `;
}

export default function UnifiedPdfTemplate(props: UnifiedPdfTemplateProps) {
  return null;
}
